import React from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Checkbox, FormControlLabel, Hidden, TextField } from '@mui/material';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import { useRegions } from 'services/Data/RegionService';
import { translate } from '@acnb/CustomComponents/DropdownItem/Translate';
import { getHighlightParts } from 'services/Utilities/NewHighlights';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import { usePatchExteriorSignageQuoteChangeShipping } from 'services/Data/ExteriorSignageQuoteService';
import ExteriorSignageQuoteTermConditions from './ExteriorSignageQuoteTermConditions';

const ExteriorSignageQuoteDeliveryCheckoutSection = ({
  exteriorSignageQuote,
  isLoading,
  validator,
  isDisabled,
  actionBtns,
}) => {
  const { t } = useTranslation();

  const { mutate: changeShipping, isPending: changingShipping } = usePatchExteriorSignageQuoteChangeShipping();
  const { data: regions, isLoading: gettingRegions } = useRegions();

  return (
    <SkeletonWrapper
      isLoading={isLoading || changingShipping || gettingRegions}
      component={
        <GridContainer>
          <GridItem xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={({ target: { checked } }) =>
                    changeShipping({ ...exteriorSignageQuote, isDealerShippingAddress: checked })
                  }
                  checked={exteriorSignageQuote?.isDealerShippingAddress || false}
                  disabled={!exteriorSignageQuote || isDisabled || exteriorSignageQuote.isDemo}
                />
              }
              label={t('isDealerShippingAddress')}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingAddress')}
              id="shippingAddress"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('shippingAddress'), exteriorSignageQuote?.shippingAddress, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...exteriorSignageQuote, shippingAddress: value });
                },
                disabled: !exteriorSignageQuote || isDisabled || exteriorSignageQuote.isDealerShippingAddress,
                defaultValue: exteriorSignageQuote?.shippingAddress ?? '',
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingCity')}
              id="shippingCity"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(t('shippingCity'), exteriorSignageQuote?.shippingCity, `required`)}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...exteriorSignageQuote, shippingCity: value });
                },
                disabled: !exteriorSignageQuote || isDisabled || exteriorSignageQuote.isDealerShippingAddress,
                defaultValue: exteriorSignageQuote?.shippingCity ?? '',
              }}
            />
          </GridItem>
          <GridItem xs={12}>
            <CustomAutocomplete
              labelText={t('regions')}
              options={regions || []}
              formControlProps={{
                fullWidth: true,
              }}
              disabled={!exteriorSignageQuote || isDisabled || exteriorSignageQuote.isDealerShippingAddress}
              error={validator.current.message(t('region'), exteriorSignageQuote?.shippingRegion_Id, `required`)}
              autocompleteProps={{
                onChange: (event, value) => {
                  changeShipping({ ...exteriorSignageQuote, shippingRegion_Id: value.id });
                },
                disableClearable: true,
                isOptionEqualToValue: (option, value) => option?.id === value,
                value: exteriorSignageQuote?.shippingRegion_Id || null,
                getOptionLabel: option => {
                  let item = regions?.find(se => se?.id === option) || option;

                  return (
                    (item &&
                      `${translate({
                        label: item?.label,
                        t,
                      })}`) ||
                    ''
                  );
                },
                filterOptions: (options, state) => {
                  var searchTerms = state.inputValue.toLowerCase().split(' ');
                  var filteredOptions = options.filter(option =>
                    searchTerms.every(searchTerm =>
                      translate({ label: option.label, t })
                        ?.toLowerCase()
                        .includes(searchTerm),
                    ),
                  );

                  return filteredOptions;
                },
                renderOption: (props, option, { inputValue }) => {
                  const result = getHighlightParts(`${translate({ label: option.label, t })}`, inputValue);

                  return (
                    <div {...props} key={option.id}>
                      {result}
                    </div>
                  );
                },
                renderInput: params => (
                  <TextField
                    {...params}
                    label={t('regions')}
                    variant="outlined"
                    error={!!validator.current.message(t('region'), exteriorSignageQuote?.shippingRegion_Id, 'required')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                      },
                    }}
                  />
                ),
              }}
            />
          </GridItem>

          <GridItem xs={12}>
            <CustomInput
              labelText={t('shippingPostalCode')}
              id="shippingPostalCode"
              formControlProps={{
                fullWidth: true,
              }}
              error={validator.current.message(
                t('shippingPostalCode'),
                exteriorSignageQuote?.shippingPostalCode,
                `required`,
              )}
              inputProps={{
                onBlur: ({ target: { value } }) => {
                  changeShipping({ ...exteriorSignageQuote, shippingPostalCode: value });
                },
                disabled: !exteriorSignageQuote || isDisabled || exteriorSignageQuote.isDealerShippingAddress,
                defaultValue: exteriorSignageQuote?.shippingPostalCode ?? '',
              }}
            />
          </GridItem>

          <Hidden mdDown>
            <GridItem xs={12}>
              <ExteriorSignageQuoteTermConditions />
            </GridItem>
            <GridItem xs={12}>
              {actionBtns
                .filter(actionBtn => !actionBtn.hidden)
                .map(actionBtn => (
                  <Button
                    sx={{ borderRadius: 0, color: '#000', mt: 4, width: '100%' }}
                    variant={actionBtn.variant}
                    color={actionBtn.color}
                    disabled={!exteriorSignageQuote || actionBtn.disabled}
                    onClick={actionBtn.action}>
                    <h1>{actionBtn.label}</h1>
                  </Button>
                ))}
            </GridItem>
          </Hidden>
        </GridContainer>
      }
    />
  );
};

export default ExteriorSignageQuoteDeliveryCheckoutSection;
